import React, { useState, useEffect, useRef } from 'react';
import { database, storage } from '../firebaseConfig';
import { ref as dbRef, onValue } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useActiveWorld } from '../hooks/useActiveWorld';
import { authenticatedFetch, CLOUD_FUNCTION_URLS } from '../utils/cloudFunctions';
import './ScreenComposerPopup.css';

const ScreenComposerPopup = ({ onClose, onInsert, recipientUrl, onGenerateBackground }) => {
  const { userId } = useActiveWorld();
  const [activeTab, setActiveTab] = useState('create');
  const [settings, setSettings] = useState({ defaultUrl: '', format: 'image', photoUrl: '', videoUrl: '' });

  // Create tab state
  const [websiteUrl, setWebsiteUrl] = useState(recipientUrl || '');
  const [format, setFormat] = useState('image');
  const [usePhoto, setUsePhoto] = useState(false);
  const [useVideo, setUseVideo] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pollingJobId, setPollingJobId] = useState(null);
  const [error, setError] = useState(null);

  // Upload tab state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const pollingRef = useRef(null);

  useEffect(() => {
    if (!userId) return;
    const settingsDbRef = dbRef(database, `/World/${userId}/settings/screenComposer`);
    const unsub = onValue(settingsDbRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const loaded = {
          defaultUrl: data.defaultUrl || '',
          format: data.format || 'image',
          photoUrl: data.photoUrl || '',
          videoUrl: data.videoUrl || '',
        };
        setSettings(loaded);
        if (!websiteUrl && loaded.defaultUrl) setWebsiteUrl(loaded.defaultUrl);
        if (loaded.videoUrl && !loaded.photoUrl) {
          setFormat('video');
        } else {
          setFormat(loaded.format);
        }
        if (loaded.photoUrl) setUsePhoto(true);
        if (loaded.videoUrl) setUseVideo(true);
      }
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const handleGenerate = async () => {
    if (!websiteUrl.trim()) {
      setError('Please enter a website URL');
      return;
    }

    if (onGenerateBackground) {
      onGenerateBackground({
        url: websiteUrl.trim(),
        format,
        photoUrl: usePhoto && settings.photoUrl ? settings.photoUrl : undefined,
        videoUrl: useVideo && settings.videoUrl ? settings.videoUrl : undefined,
      });
      onClose();
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const targetUrl = CLOUD_FUNCTION_URLS.screenComposerProxy();
      const payload = {
        action: 'generate',
        url: websiteUrl.trim(),
        format,
        photo_url: usePhoto && settings.photoUrl ? settings.photoUrl : undefined,
        video_url: useVideo && settings.videoUrl ? settings.videoUrl : undefined,
      };
      console.log('[SC] Calling generate:', targetUrl, payload);

      const response = await authenticatedFetch(targetUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      console.log('[SC] Response status:', response.status, response.statusText);
      const text = await response.text();
      console.log('[SC] Response body:', text);

      let data;
      try { data = JSON.parse(text); } catch { throw new Error('Invalid JSON from server: ' + text.substring(0, 200)); }
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}: ${text.substring(0, 200)}`);

      console.log('[SC] Job ID:', data.job_id);
      if (data.job_id) {
        setPollingJobId(data.job_id);
        startPolling(data.job_id);
      } else {
        throw new Error('No job_id in response: ' + JSON.stringify(data));
      }
    } catch (err) {
      console.error('[SC] Generate error:', err);
      setError(err.message || 'Failed to generate');
      setGenerating(false);
    }
  };

  const startPolling = (jobId) => {
    let attempts = 0;
    pollingRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 60) {
        clearInterval(pollingRef.current);
        setError('Generation timed out. Please try again.');
        setGenerating(false);
        setPollingJobId(null);
        return;
      }

      try {
        const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.screenComposerProxy(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'poll', jobId }),
        });

        console.log('[SC] Poll #' + attempts, '→ status:', response.status);
        const data = await response.json();
        console.log('[SC] Poll data:', JSON.stringify(data));

        if (data.status === 'completed') {
          clearInterval(pollingRef.current);
          await downloadResult(jobId, data.output_format);
        } else if (data.status === 'failed') {
          clearInterval(pollingRef.current);
          setError('Generation failed: ' + (data.error || 'Unknown reason'));
          setGenerating(false);
          setPollingJobId(null);
        }
      } catch (pollErr) {
        console.error('[SC] Poll error #' + attempts + ':', pollErr.message);
        if (attempts > 5) {
          clearInterval(pollingRef.current);
          setError('Connection to Screen Composer failed: ' + pollErr.message);
          setGenerating(false);
          setPollingJobId(null);
        }
      }
    }, 2000);
  };

  const downloadResult = async (jobId, outputFormat) => {
    try {
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.screenComposerProxy(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'download', jobId }),
      });

      const data = await response.json();
      if (data.success && data.data) {
        const contentType = data.contentType || (outputFormat === 'video' ? 'image/gif' : 'image/jpeg');
        const ext = contentType.includes('gif') ? 'gif' : contentType.includes('png') ? 'png' : 'jpg';
        const byteString = atob(data.data);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
        const blob = new Blob([ab], { type: contentType });

        const fileRef = storageRef(storage, `screenComposer/${userId}/generated_${Date.now()}.${ext}`);
        await uploadBytes(fileRef, blob);
        const url = await getDownloadURL(fileRef);
        onInsert({ type: 'url', url, contentType });
      }
    } catch (err) {
      console.error('Download/upload error:', err);
      setError('Failed to process result');
    } finally {
      setGenerating(false);
      setPollingJobId(null);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    if (isImage && file.size > 1 * 1024 * 1024) {
      setError('Image must be under 1 MB');
      return;
    }
    if (isVideo && file.size > 5 * 1024 * 1024) {
      setError('Video must be under 5 MB');
      return;
    }
    if (!isImage && !isVideo) {
      setError('Please select an image or video file');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const fileRef = storageRef(storage, `screenComposer/${userId}/email_${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      onInsert({ type: 'url', url, contentType: file.type });
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="scPopup-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="scPopup-modal">
        <div className="scPopup-header">
          <h3>Screen Composer</h3>
          <button className="scPopup-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="scPopup-tabs">
          <button
            className={`scPopup-tab ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
              <path d="M8 21H16M12 17V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Create
          </button>
          <button
            className={`scPopup-tab ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M21 15V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M17 8L12 3L7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Upload
          </button>
        </div>

        {error && (
          <div className="scPopup-error">
            {error}
            <button onClick={() => setError(null)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        )}

        <div className="scPopup-body">
          {activeTab === 'create' && (
            <div className="scPopup-create">
              <div className="scPopup-field">
                <label>Website URL</label>
                <input
                  type="url"
                  className="scPopup-input"
                  placeholder="https://example.com"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  disabled={generating}
                />
              </div>

              <div className="scPopup-field">
                <label>Format</label>
                <div className="scPopup-format-row">
                  <button
                    className={`scPopup-format-btn ${format === 'image' ? 'active' : ''}`}
                    onClick={() => setFormat('image')}
                    disabled={generating}
                  >
                    Screenshot
                  </button>
                  <button
                    className={`scPopup-format-btn ${format === 'video' ? 'active' : ''}`}
                    onClick={() => setFormat('video')}
                    disabled={generating}
                  >
                    Scroll GIF
                  </button>
                </div>
              </div>

              {(settings.photoUrl || settings.videoUrl) && (
                <div className="scPopup-overlays">
                  <label>Overlays</label>
                  {settings.photoUrl && (
                    <label className={`scPopup-overlay-option ${usePhoto ? 'active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={usePhoto}
                        onChange={(e) => setUsePhoto(e.target.checked)}
                        disabled={generating}
                      />
                      <img src={settings.photoUrl} alt="Photo" className="scPopup-overlay-thumb" />
                      <span>Profile Photo</span>
                    </label>
                  )}
                  {settings.videoUrl && (
                    <label className={`scPopup-overlay-option ${useVideo ? 'active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={useVideo}
                        onChange={(e) => setUseVideo(e.target.checked)}
                        disabled={generating}
                      />
                      <div className="scPopup-overlay-video-thumb">
                        <video src={settings.videoUrl} muted className="scPopup-overlay-video-preview" />
                        <div className="scPopup-overlay-video-play">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                            <polygon points="10,8 16,12 10,16" fill="currentColor"/>
                          </svg>
                        </div>
                      </div>
                      <span>Webcam Video</span>
                    </label>
                  )}
                </div>
              )}

              <button
                className="scPopup-generate-btn"
                onClick={handleGenerate}
                disabled={generating || !websiteUrl.trim()}
              >
                {generating ? (
                  <>
                    <span className="scPopup-spinner"></span>
                    {pollingJobId ? 'Generating...' : 'Starting...'}
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Generate
                  </>
                )}
              </button>
            </div>
          )}

          {activeTab === 'upload' && (
            <div className="scPopup-upload">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <div
                className="scPopup-dropzone"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? (
                  <>
                    <span className="scPopup-spinner large"></span>
                    <p>Uploading...</p>
                  </>
                ) : (
                  <>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                      <path d="M21 15V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M17 8L12 3L7 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 3V15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <p><strong>Click to upload</strong></p>
                    <span className="scPopup-dropzone-hint">Image up to 1 MB or Video up to 5 MB</span>
                    <span className="scPopup-dropzone-formats">JPG, PNG, GIF, WebP, MP4, WebM</span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScreenComposerPopup;
